import { supabase } from '../supabase';
import { isMaintenanceTime, getMontrealTime } from '../maintenanceUtils';

export interface PointSettings {
  pts_goal_att: number;
  pts_ass_att: number;
  pts_goal_def: number;
  pts_ass_def: number;
  pts_goal_g: number;
  pts_win_g: number;
  pts_shu_g: number;
  pts_otlosse_g: number;
}

export interface LivePlayerStats {
  playerId: number;
  playerName: string;
  playerPhoto: string;
  position: string;
  goals: number;
  assists: number;
  wins: number;
  shutouts: number;
  otlosses: number;
  totalPoints: number;
}

export interface LiveStandingsRow {
  user_id: number;
  username: string;
  totgoals: number;
  totassists: number;
  totgoals_goalie: number;
  totassists_goalie: number;
  totwins_goalie: number;
  totshutout_goalie: number;
  tototlosses_goalie: number;
  playerStats: LivePlayerStats[];
}

export interface FollowedStandingsRow {
  rank: number;
  user_id: number;
  username: string;
  nhl_team_abbrev: string | null;
  totgoals: number;
  totassists: number;
  totgoals_goalie: number;
  totassists_goalie: number;
  totwins_goalie: number;
  totshutout_goalie: number;
  tototlosses_goalie: number;
}

interface StandingsAccumulator {
  totgoals: number;
  totassists: number;
  totgoals_goalie: number;
  totassists_goalie: number;
  totwins_goalie: number;
  totshutout_goalie: number;
  tototlosses_goalie: number;
}

interface RosterEntry {
  userId: number;
  isGoalie: boolean;
  isDefense: boolean;
  playerName: string;
  playerPhoto: string;
  position: string;
}

interface ScoreboardAssist {
  playerId?: number | string | null;
}

interface ScoreboardGoal {
  playerId?: number | string | null;
  assists?: ScoreboardAssist[] | null;
}

interface ScoreboardGame {
  id?: number | string | null;
  gameState?: string | null;
  goals?: ScoreboardGoal[] | null;
}

interface ScoreboardResponse {
  games?: ScoreboardGame[] | null;
}

interface BoxscoreGoalieStats {
  goalsAgainst?: number | string | null;
  timeOnIce?: string | null;
  toi?: string | null;
  shotsAgainst?: number | string | null;
}

interface BoxscoreGoalie {
  playerId?: number | string | null;
  decision?: string | null;
  goalsAgainst?: number | string | null;
  goalAgainst?: number | string | null;
  timeOnIce?: string | null;
  toi?: string | null;
  shotsAgainst?: number | string | null;
  stats?: BoxscoreGoalieStats | null;
}

interface BoxscoreTeam {
  goalies?: BoxscoreGoalie[] | null;
}

interface BoxscoreResponse {
  gameState?: string | null;
  homeTeam?: BoxscoreTeam | null;
  awayTeam?: BoxscoreTeam | null;
}

type SupabaseRecord = Record<string, unknown>;

function toSupabaseRecord(value: unknown): SupabaseRecord {
  if (value && typeof value === 'object') {
    return value as SupabaseRecord;
  }
  return {};
}

const FINAL_GAME_STATES = new Set(['OFF', 'FINAL', 'FINAL_OT', 'FINAL_SO']);

function createEmptyTotals(): StandingsAccumulator {
  return {
    totgoals: 0,
    totassists: 0,
    totgoals_goalie: 0,
    totassists_goalie: 0,
    totwins_goalie: 0,
    totshutout_goalie: 0,
    tototlosses_goalie: 0
  };
}

function ensureTotals(map: Map<number, StandingsAccumulator>, userId: number): StandingsAccumulator {
  if (!map.has(userId)) {
    map.set(userId, createEmptyTotals());
  }
  return map.get(userId)!;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  return null;
}

function normalizePositionCode(value: unknown): string | null {
  if (!value) {
    return null;
  }
  return String(value).toUpperCase();
}

function isDefensePosition(positionCode?: string | null): boolean {
  if (!positionCode) {
    return false;
  }
  const normalized = positionCode.toUpperCase();
  return normalized === 'D' || normalized === 'LD' || normalized === 'RD' || normalized === 'DEF';
}

function isTruthy(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return Boolean(value);
}

function parseTimeToSeconds(value?: string | null): number {
  if (!value) {
    return 0;
  }

  const segments = value.split(':').map((segment) => Number(segment));
  if (segments.some((segment) => Number.isNaN(segment))) {
    return 0;
  }

  if (segments.length === 3) {
    return segments[0] * 3600 + segments[1] * 60 + segments[2];
  }
  if (segments.length === 2) {
    return segments[0] * 60 + segments[1];
  }
  return segments[0];
}

function getScoreboardDateString(): string {
  const inMaintenance = isMaintenanceTime();
  
  // Si on utilise 'now' et qu'on n'est pas en maintenance, continuer avec 'now'
  if (!inMaintenance) {
    console.log(`📅 Scoreboard NHL - Date utilisée: now (maintenance: false)`);
    return 'now';
  }
  
  // En mode maintenance, calculer la date de la veille
  const now = new Date();
  
  // Utiliser toLocaleString avec en-US pour obtenir une chaîne compatible
  const montrealTimeStr = now.toLocaleString('en-US', { timeZone: 'America/Toronto' });
  const montrealTime = new Date(montrealTimeStr);
  
  // Reculer d'1 jour
  montrealTime.setDate(montrealTime.getDate() - 1);
  
  const year = montrealTime.getFullYear();
  const month = String(montrealTime.getMonth() + 1).padStart(2, '0');
  const day = String(montrealTime.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  
  console.log(`📅 Scoreboard NHL - Date utilisée: ${dateStr} (maintenance: true, heure Montréal: ${montrealTime.toLocaleTimeString('fr-CA')})`);
  return dateStr;
}

async function fetchJsonWithFallback<T>(url: string): Promise<T | null> {
  if (typeof fetch !== 'function') {
    return null;
  }

  try {
    // Use Supabase Edge Function to fetch NHL data
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (supabaseUrl) {
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/nhl-data`;
      const response = await fetch(`${edgeFunctionUrl}?endpoint=${encodeURIComponent(url)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      if (response.ok) {
        return (await response.json()) as T;
      }
    }
  } catch (error) {
    console.warn('Edge function fetch failed, falling back to direct fetch:', error);
  }

  // Fallback to direct fetch (will likely fail due to CORS)
  try {
    const response = await fetch(url, { cache: 'no-store' });
    if (response.ok) {
      return (await response.json()) as T;
    }
  } catch (error) {
    console.warn('Direct fetch failed:', error);
  }

  return null;
}

async function getStandingsUsers(userIds: number[]) {
  const uniqueUserIds = [...new Set(userIds)];
  const users: Array<{ user_id: number; username: string }> = [];

  for (let offset = 0; offset < uniqueUserIds.length; offset += 400) {
    const requests = [];
    for (let batchOffset = offset; batchOffset < Math.min(offset + 400, uniqueUserIds.length); batchOffset += 100) {
      requests.push(supabase
        .from('users')
        .select('user_id, username')
        .in('user_id', uniqueUserIds.slice(batchOffset, batchOffset + 100)));
    }

    const results = await Promise.all(requests);
    for (const { data, error } of results) {
      if (error) throw error;
      users.push(...(data ?? []));
    }
  }

  return users;
}

async function fetchFollowedStandings(
  functionName: 'get_followed_standings' | 'get_followed_daily_standings',
  leagueId: number,
  followerUserId: number
) {
  try {
    const { data, error } = await supabase.rpc(functionName, {
      p_league_id: leagueId,
      p_follower_user_id: followerUserId
    });

    if (error) throw error;
    return { data: (data ?? []) as FollowedStandingsRow[], error: null };
  } catch (error: unknown) {
    console.error(`Error fetching ${functionName}:`, error);
    return { data: null, error };
  }
}

export interface MonthlyStandingsMonth {
  year: number;
  month: number;
}

// Liste des mois réellement disponibles pour une ligue (générés depuis standing_backup)
export async function getMonthlyStandingsMonths(leagueId: number) {
  try {
    const { data, error } = await supabase.rpc('get_monthly_standings_months', {
      p_league_id: leagueId
    });
    if (error) throw error;
    return { data: (data ?? []) as MonthlyStandingsMonth[], error: null };
  } catch (error: unknown) {
    console.error('Error fetching monthly standings months:', error);
    return { data: null, error };
  }
}

// Classement mensuel complet (même structure que `standing`), enrichi des usernames/logos
export async function getMonthlyStandings(leagueId: number, year: number, month: number) {
  try {
    const { data, error } = await supabase.rpc('get_monthly_standings', {
      p_league_id: leagueId,
      p_year: year,
      p_month: month
    });
    if (error) throw error;

    const standingsData = (data ?? []) as Array<{
      user_id: number;
      totgoals: number;
      totassists: number;
      totgoals_goalie: number;
      totassists_goalie: number;
      totwins_goalie: number;
      totshutout_goalie: number;
      tototlosses_goalie: number;
    }>;

    if (standingsData.length === 0) {
      return { data: [], error: null };
    }

    const userIds = standingsData.map((standing) => standing.user_id);
    const userData = await getStandingsUsers(userIds);

    const { data: logoData } = await supabase
      .from('leagues_users')
      .select('user_id, nhl_team_abbrev')
      .eq('league_id', leagueId);

    const logoMap = new Map<number, string | null>(
      (logoData ?? []).map((row: { user_id: number; nhl_team_abbrev?: string | null }) => [
        row.user_id,
        row.nhl_team_abbrev ?? null
      ])
    );

    const standingsWithUsernames = standingsData.map((standing) => ({
      ...standing,
      username: userData?.find((u) => u.user_id === standing.user_id)?.username || 'Unknown User',
      nhl_team_abbrev: logoMap.get(standing.user_id) ?? null
    }));

    return { data: standingsWithUsernames, error: null };
  } catch (error: unknown) {
    console.error('Error fetching monthly standings:', error);
    return { data: null, error };
  }
}

// Classement mensuel fenêtré autour de l'utilisateur (rank +/- before/after)
export async function getMonthlyStandingsWindow(
  leagueId: number,
  userId: number,
  year: number,
  month: number,
  before = 5,
  after = 5
) {
  try {
    const { data, error } = await supabase.rpc('get_monthly_standings_window', {
      p_league_id: leagueId,
      p_user_id: userId,
      p_year: year,
      p_month: month,
      p_before: before,
      p_after: after
    });
    if (error) throw error;
    return { data, error: null };
  } catch (error: unknown) {
    console.error('Error fetching monthly standings window:', error);
    return { data: null, error };
  }
}

export function getFollowedStandings(leagueId: number, followerUserId: number) {
  return fetchFollowedStandings('get_followed_standings', leagueId, followerUserId);
}

export function getFollowedDailyStandings(leagueId: number, followerUserId: number) {
  return fetchFollowedStandings('get_followed_daily_standings', leagueId, followerUserId);
}

export async function getStandings(leagueId: number, poolStartDate?: string | null) {
  try {
    let standingsData: Array<{
      user_id: number;
      totgoals: number;
      totassists: number;
      totgoals_goalie: number;
      totassists_goalie: number;
      totwins_goalie: number;
      totshutout_goalie: number;
      tototlosses_goalie: number;
    }> | null = null;

    if (poolStartDate) {
      // Use RPC that computes delta from pool_start_date via standing_backup
      const { data, error } = await supabase
        .rpc('get_standings_from_pool_start', { p_league_id: leagueId });
      if (error) throw error;
      standingsData = data;
    } else {
      const { data, error } = await supabase
        .from('standing')
        .select(
          'user_id, totgoals, totassists, totgoals_goalie, totassists_goalie, totwins_goalie, totshutout_goalie, tototlosses_goalie'
        )
        .eq('league_id', leagueId)
        .order('totgoals', { ascending: false });

      if (error) throw error;
      standingsData = data;
    }

    // After getting the standings, fetch the usernames
    if (standingsData) {
      const userIds = standingsData.map((standing) => standing.user_id);
      const userData = await getStandingsUsers(userIds);

      // Combine the data
      const standingsWithUsernames = standingsData.map((standing) => {
        const user = userData?.find((u) => u.user_id === standing.user_id);
        return {
          ...standing,
          username: user?.username || 'Unknown User',
        };
      });

      // Fetch NHL team logos from leagues_users for this league
      const { data: logoData } = await supabase
        .from('leagues_users')
        .select('user_id, nhl_team_abbrev')
        .eq('league_id', leagueId);

      const logoMap = new Map<number, string | null>(
        (logoData ?? []).map((row: { user_id: number; nhl_team_abbrev?: string | null }) => [
          row.user_id,
          row.nhl_team_abbrev ?? null,
        ])
      );

      const standingsWithLogos = standingsWithUsernames.map((standing) => ({
        ...standing,
        nhl_team_abbrev: logoMap.get(standing.user_id) ?? null,
      }));

      return { data: standingsWithLogos, error: null };
    }

    return { data: standingsData, error: null };
  } catch (error: unknown) {
    console.error('Error fetching standings:', error);
    return { data: null, error };
  }
}

export async function getStandingsWindow(leagueId: number, userId: number, before = 5, after = 5) {
  try {
    const { data, error } = await supabase.rpc('get_standings_window', {
      p_league_id: leagueId,
      p_user_id: userId,
      p_before: before,
      p_after: after
    });

    if (error) {
      // If RPC not found or other error, surface it to caller for fallback
      throw error;
    }

    // Data should already include username and nhl_team_abbrev from the RPC
    return { data, error: null };
  } catch (error: unknown) {
    console.error('Error fetching standings window:', error);
    return { data: null, error };
  }
}

export async function getDailyStandings(leagueId: number) {
  try {
    const currentHourUTC = new Date().getUTCHours();

    const currentHourMontreal = new Date().toLocaleTimeString("en-CA", {
      timeZone: "America/Toronto",
      hour: "2-digit",
      hour12: false
    });

    const currentHourQc = parseInt(currentHourMontreal); // pour avoir un entier comme getUTCHours()
    
  let playerData: Array<{ user_id: number; goals: number; assists: number }> | null = null;
  let goalieData: Array<{ user_id: number; wins: number; shutouts: number; goals: number; assists: number; otlosses?: number }> | null = null;

    // Determine which data source to use based on UTC time
    if (currentHourQc >= 3) {
      console.log("rentre dans ma table");
      console.log(currentHourQc);
      console.log(currentHourUTC);
      // After 10:00 UTC - use backup tables
      const { data: pData, error: pError } = await supabase
        .from('get_points_made_looped_backup')
        .select('*')
        .eq('league_id', leagueId);
      
      if (pError) throw pError;
      playerData = (pData ?? []) as Array<{ user_id: number; goals: number; assists: number }>;

      const { data: gData, error: gError } = await supabase
        .from('get_points_made_goalie_looped_backup')
        .select('*')
        .eq('league_id', leagueId);
      
      if (gError) throw gError;
      goalieData = (gData ?? []) as Array<{ user_id: number; wins: number; shutouts: number; goals: number; assists: number; otlosses?: number }>;
    } else {
      // Before 10:00 UTC - use functions
       console.log("rentre dans ma function");
       console.log(currentHourQc);
       console.log(currentHourUTC);
      const { data: pData, error: pError } = await supabase
        .rpc('get_points_made_looped', { p_league_id: leagueId });
      
      if (pError) throw pError;
      playerData = (pData ?? []) as Array<{ user_id: number; goals: number; assists: number }>;

      const { data: gData, error: gError } = await supabase
        .rpc('get_points_made_goalie_looped', { p_league_id: leagueId });
      
      if (gError) throw gError;
      goalieData = (gData ?? []) as Array<{ user_id: number; wins: number; shutouts: number; goals: number; assists: number; otlosses?: number }>;
    }

    // Get usernames for all users
    const allUserIds = [...new Set([
      ...(playerData || []).map((p) => p.user_id),
      ...(goalieData || []).map((g) => g.user_id)
    ])];

    const userData = await getStandingsUsers(allUserIds);

    // Combine player and goalie data
    const combinedData = allUserIds.map((userId) => {
      const user = userData?.find((u) => u.user_id === userId);
      const player = playerData?.find((p) => p.user_id === userId) || { goals: 0, assists: 0 };
      const goalie =
        goalieData?.find((g) => g.user_id === userId) ||
        { wins: 0, shutouts: 0, goals: 0, assists: 0, otlosses: 0 };

      return {
        user_id: userId,
        username: user?.username || 'Unknown User',
        totgoals: player.goals || 0,
        totassists: player.assists || 0,
        totgoals_goalie: goalie.goals || 0,
        totassists_goalie: goalie.assists || 0,
        totwins_goalie: goalie.wins || 0,
        totshutout_goalie: goalie.shutouts || 0,
        tototlosses_goalie: goalie.otlosses || 0
      };
    });

    return { data: combinedData, error: null };
  } catch (error: unknown) {
    console.error('Error fetching daily standings:', error);
    return { data: null, error };
  }
}

export async function getDailyStandingsWindow(leagueId: number, userId: number, before = 5, after = 5) {
  try {
    // Try server-side RPC first (most efficient — no full dataset transfer)
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_daily_standings_window', {
      p_league_id: leagueId,
      p_user_id: userId,
      p_before: before,
      p_after: after
    });

    if (!rpcError && Array.isArray(rpcData)) {
      return { data: rpcData, error: null };
    }

    // RPC not available — fallback to client-side windowing from backup tables
    console.warn('get_daily_standings_window RPC failed, falling back to client-side:', rpcError);

    const { data: pData, error: pError } = await supabase
      .from('get_points_made_looped_backup')
      .select('*')
      .eq('league_id', leagueId);

    const { data: gData, error: gError } = await supabase
      .from('get_points_made_goalie_looped_backup')
      .select('*')
      .eq('league_id', leagueId);

    if (pError && gError) {
      throw pError;
    }

    const playerData = (pData ?? []) as Array<{ user_id: number; goals: number; assists: number }>;
    const goalieData = (gData ?? []) as Array<{ user_id: number; wins: number; shutouts: number; goals: number; assists: number; otlosses?: number }>;

    const allUserIds = [...new Set([
      ...playerData.map((p) => p.user_id),
      ...goalieData.map((g) => g.user_id)
    ])];

    if (allUserIds.length === 0) {
      return { data: [], error: null };
    }

    const userData = await getStandingsUsers(allUserIds);

    const withTotals = allUserIds.map((uid) => {
      const player = playerData.find((p) => p.user_id === uid) || { goals: 0, assists: 0 };
      const goalie = goalieData.find((g) => g.user_id === uid) || { wins: 0, shutouts: 0, goals: 0, assists: 0, otlosses: 0 };
      const totgoals = player.goals || 0;
      const totassists = player.assists || 0;
      const totgoals_goalie = goalie.goals || 0;
      const totassists_goalie = goalie.assists || 0;
      const totwins_goalie = goalie.wins || 0;
      const totshutout_goalie = goalie.shutouts || 0;
      const tototlosses_goalie = goalie.otlosses || 0;
      return {
        user_id: uid,
        username: userData?.find((u) => u.user_id === uid)?.username || 'Unknown User',
        totgoals, totassists, totgoals_goalie, totassists_goalie, totwins_goalie, totshutout_goalie, tototlosses_goalie,
        total_points: totgoals + totassists + totgoals_goalie + totassists_goalie + totwins_goalie + totshutout_goalie + tototlosses_goalie
      };
    });

    withTotals.sort((a, b) => b.total_points - a.total_points || a.user_id - b.user_id);

    const userIndex = withTotals.findIndex((r) => Number(r.user_id) === Number(userId));
    let start = 0;
    let end = Math.min(withTotals.length - 1, before + after);
    if (userIndex >= 0) {
      start = Math.max(0, userIndex - before);
      end = Math.min(withTotals.length - 1, userIndex + after);
    }

    const windowRows = withTotals.slice(start, end + 1).map((r, idx) => ({
      rank: start + idx + 1,
      user_id: r.user_id,
      username: r.username,
      totgoals: r.totgoals,
      totassists: r.totassists,
      totgoals_goalie: r.totgoals_goalie,
      totassists_goalie: r.totassists_goalie,
      totwins_goalie: r.totwins_goalie,
      totshutout_goalie: r.totshutout_goalie,
      tototlosses_goalie: r.tototlosses_goalie
    }));

    return { data: windowRows, error: null };
  } catch (error: unknown) {
    console.error('Error fetching daily standings window:', error);
    return { data: null, error };
  }
}

export async function getLiveStandings(leagueId: number, pointSettings: PointSettings, userIds?: number[]) {
  try {
    console.log('🔄 Starting getLiveStandings for league:', leagueId);
    console.log('📊 Point settings:', pointSettings);
    
    // Détecter si on est en période de maintenance
    const inMaintenance = isMaintenanceTime();
    
    // Choisir les vues selon le mode maintenance
    const playerView = inMaintenance ? 'getmyteamplayerlastday' : 'getmyteamplayer';
    const goalieView = inMaintenance ? 'getmyteamgoalielastday' : 'getmyteamgoalie';
    
    console.log(`🔍 Mode maintenance: ${inMaintenance ? 'OUI' : 'NON'} (${getMontrealTime()})`);
    console.log(`📊 Vues utilisées: joueurs=${playerView}, gardiens=${goalieView}`);
    
    // If userIds provided, limit the player/goalie queries to those users to reduce load
    const leagueUsersQuery = supabase.from('leagues_users').select('user_id').eq('league_id', leagueId);
    const skatersQuery = supabase.from(playerView).select('*').eq('league_id', leagueId);
    const goaliesQuery = supabase.from(goalieView).select('*').eq('league_id', leagueId);

    if (Array.isArray(userIds) && userIds.length > 0) {
      skatersQuery.in('user_id', userIds);
      goaliesQuery.in('user_id', userIds);
      leagueUsersQuery.in('user_id', userIds);
    }

    const [leagueUsersResult, skatersResult, goaliesResult] = await Promise.all([
      leagueUsersQuery,
      skatersQuery,
      goaliesQuery
    ]);

    console.log('👥 League users result:', leagueUsersResult);
    console.log('🏒 Skaters result:', skatersResult);
    console.log('🥅 Goalies result:', goaliesResult);

    if (leagueUsersResult.error) throw leagueUsersResult.error;
    if (skatersResult.error) throw skatersResult.error;
    if (goaliesResult.error) throw goaliesResult.error;

    const totalsMap = new Map<number, StandingsAccumulator>();
    const rosterMap = new Map<number, RosterEntry>();

    const registerUser = (rawUserId: unknown) => {
      const userId = toNumber(rawUserId);
      if (!userId) {
        return;
      }
      ensureTotals(totalsMap, userId);
    };

    const leagueUserRows: ReadonlyArray<{ user_id: unknown }> = Array.isArray(leagueUsersResult.data)
      ? leagueUsersResult.data.map((row) => {
          const record = toSupabaseRecord(row);
          return { user_id: record['user_id'] };
        })
      : [];

    leagueUserRows.forEach((row) => registerUser(row.user_id));

    const registerPlayer = (row: SupabaseRecord, forceGoalie = false) => {
      const playerId = toNumber(row['player_id'] ?? row['playerid'] ?? row['id']);
      const userId = toNumber(row['user_id']);

      if (!playerId || !userId) {
        return;
      }

      const isMainClub = row['isnhl'] === undefined || row['isnhl'] === null ? true : isTruthy(row['isnhl']);
      const isScratch = isTruthy(row['is_scratch']);

      if (!isMainClub || isScratch) {
        return;
      }

      const positionCode = normalizePositionCode(row['positioncode'] ?? row['position_code'] ?? row['position']);
      const isGoalie =
        forceGoalie ||
        isTruthy(row['is_goalie']) ||
        positionCode === 'G' ||
        Boolean(row['goaliefullname']);
      const isDefense = !isGoalie && isDefensePosition(positionCode);

      // Extract player name and photo
      const playerName = String(
        row['skaterfullname'] || 
        row['goaliefullname'] || 
        row['playerfullname'] || 
        row['player_name'] || 
        row['fullname'] || 
        `Player ${playerId}`
      );

      const teamAbbrev = String(row['teamabbrevs'] || row['team'] || 'UNK');
      
      const playerPhoto = String(
        row['playerphoto'] ||
        row['goaliephoto'] ||
        row['player_photo'] ||
        row['photo'] ||
        `https://assets.nhle.com/mugs/nhl/20232024/${teamAbbrev}/${playerId}.png`
      );

      rosterMap.set(playerId, {
        userId,
        isGoalie,
        isDefense,
        playerName,
        playerPhoto,
        position: positionCode || (isGoalie ? 'G' : 'F')
      });

      ensureTotals(totalsMap, userId);
    };

    const skaterRows: SupabaseRecord[] = Array.isArray(skatersResult.data)
      ? skatersResult.data.map((row) => toSupabaseRecord(row))
      : [];
    const goalieRows: SupabaseRecord[] = Array.isArray(goaliesResult.data)
      ? goaliesResult.data.map((row) => toSupabaseRecord(row))
      : [];

    skaterRows.forEach((row) => registerPlayer(row));
    goalieRows.forEach((row) => registerPlayer(row, true));

    // Map to track individual player stats
    const playerStatsMap = new Map<number, LivePlayerStats>();

    // Initialize player stats for all roster players
    rosterMap.forEach((entry, playerId) => {
      playerStatsMap.set(playerId, {
        playerId,
        playerName: entry.playerName,
        playerPhoto: entry.playerPhoto,
        position: entry.position,
        goals: 0,
        assists: 0,
        wins: 0,
        shutouts: 0,
        otlosses: 0,
        totalPoints: 0
      });
    });

    const applyGoalContribution = (playerId: number) => {
      const rosterEntry = rosterMap.get(playerId);
      if (!rosterEntry) {
        return;
      }

      const totals = ensureTotals(totalsMap, rosterEntry.userId);
      const playerStats = playerStatsMap.get(playerId);

      if (rosterEntry.isGoalie) {
        const points = pointSettings.pts_goal_g;
        totals.totgoals_goalie += points;
        if (playerStats) {
          playerStats.goals += 1;
          playerStats.totalPoints += points;
        }
      } else {
        const goalValue = rosterEntry.isDefense ? pointSettings.pts_goal_def : pointSettings.pts_goal_att;
        totals.totgoals += goalValue;
        if (playerStats) {
          playerStats.goals += 1;
          playerStats.totalPoints += goalValue;
        }
      }
    };

    const applyAssistContribution = (playerId: number) => {
      const rosterEntry = rosterMap.get(playerId);
      if (!rosterEntry) {
        return;
      }

      const totals = ensureTotals(totalsMap, rosterEntry.userId);
      const playerStats = playerStatsMap.get(playerId);

      if (rosterEntry.isGoalie) {
        const points = pointSettings.pts_ass_att;
        totals.totassists_goalie += points;
        if (playerStats) {
          playerStats.assists += 1;
          playerStats.totalPoints += points;
        }
      } else {
        const assistValue = rosterEntry.isDefense ? pointSettings.pts_ass_def : pointSettings.pts_ass_att;
        totals.totassists += assistValue;
        if (playerStats) {
          playerStats.assists += 1;
          playerStats.totalPoints += assistValue;
        }
      }
    };

    let scoreboardGames: ScoreboardGame[] = [];

    try {
      const scoreboardDate = getScoreboardDateString();
      console.log('📅 Fetching scoreboard for date:', scoreboardDate);
      
      const scoreboardData = await fetchJsonWithFallback<ScoreboardResponse>(
        `https://api-web.nhle.com/v1/score/${scoreboardDate}`
      );

      console.log('🏒 Scoreboard data:', scoreboardData);
      
      if (Array.isArray(scoreboardData?.games)) {
        scoreboardGames = scoreboardData.games.filter((game): game is ScoreboardGame => Boolean(game));
        console.log('🎮 Found games:', scoreboardGames.length);
      }
    } catch (error) {
      console.error('❌ Error fetching NHL scoreboard:', error);
    }

    scoreboardGames.forEach((game) => {
      const goalEvents = Array.isArray(game.goals) ? game.goals : [];

      goalEvents.forEach((goal) => {
        const scorerId = toNumber(goal?.playerId);
        if (scorerId) {
          applyGoalContribution(scorerId);
        }

        const assists = Array.isArray(goal.assists) ? goal.assists : [];
        assists.forEach((assist) => {
          const assistId = toNumber(assist?.playerId);
          if (assistId) {
            applyAssistContribution(assistId);
          }
        });
      });
    });

    if (scoreboardGames.length > 0) {
      const boxscoreResults = await Promise.allSettled(
        scoreboardGames
          .filter((game) => toNumber(game?.id))
          .map(async (game): Promise<{ game: ScoreboardGame; boxscore: BoxscoreResponse | null }> => {
            try {
              const boxscoreJson = await fetchJsonWithFallback<BoxscoreResponse>(
                `https://api-web.nhle.com/v1/gamecenter/${game.id}/boxscore`
              );

              return { game, boxscore: boxscoreJson };
            } catch (error) {
              console.error('Error fetching NHL boxscore:', error);
              throw error;
            }
          })
      );

      boxscoreResults.forEach((result) => {
        if (result.status !== 'fulfilled') {
          return;
        }

        const { game, boxscore } = result.value;

        if (!boxscore) {
          return;
        }

        const gameState = String(game?.gameState || '').toUpperCase();
        const boxscoreState = String(boxscore?.gameState || '').toUpperCase();
        const isFinal = FINAL_GAME_STATES.has(gameState) || FINAL_GAME_STATES.has(boxscoreState);

        const teamBlocks = [boxscore?.homeTeam, boxscore?.awayTeam].filter(Boolean) as BoxscoreTeam[];
        teamBlocks.forEach((team) => {
          const goalies = Array.isArray(team?.goalies) ? team.goalies : [];

          goalies.forEach((goalie) => {
            const playerId = toNumber(goalie?.playerId);
            if (!playerId) {
              return;
            }

            const rosterEntry = rosterMap.get(playerId);
            if (!rosterEntry || !rosterEntry.isGoalie) {
              return;
            }

            const totals = ensureTotals(totalsMap, rosterEntry.userId);
            const playerStats = playerStatsMap.get(playerId);
            const decision = typeof goalie?.decision === 'string' ? goalie.decision.toLowerCase() : '';

            if (decision === 'win') {
              const points = pointSettings.pts_win_g;
              totals.totwins_goalie += points;
              if (playerStats) {
                playerStats.wins += 1;
                playerStats.totalPoints += points;
              }
            }

            if (decision === 'ot') {
              const points = pointSettings.pts_otlosse_g;
              totals.tototlosses_goalie += points;
              if (playerStats) {
                playerStats.otlosses += 1;
                playerStats.totalPoints += points;
              }
            }

            if (isFinal) {
              const goalsAgainst = toNumber(
                goalie?.goalsAgainst ?? goalie?.stats?.goalsAgainst ?? goalie?.goalAgainst
              ) ?? 0;

              const toiRaw =
                (typeof goalie?.toi === 'string' && goalie.toi) ||
                (typeof goalie?.timeOnIce === 'string' && goalie.timeOnIce) ||
                (typeof goalie?.stats?.toi === 'string' && goalie.stats.toi) ||
                (typeof goalie?.stats?.timeOnIce === 'string' && goalie.stats.timeOnIce) ||
                null;

              const toiSeconds = parseTimeToSeconds(toiRaw);
              const shotsAgainst = toNumber(goalie?.shotsAgainst ?? goalie?.stats?.shotsAgainst) ?? 0;

              if (goalsAgainst === 0 && (toiSeconds >= 3500 || shotsAgainst > 0)) {
                const points = pointSettings.pts_shu_g;
                totals.totshutout_goalie += points;
                if (playerStats) {
                  playerStats.shutouts += 1;
                  playerStats.totalPoints += points;
                }
              }
            }
          });
        });
      });
    }

    const allUserIds = Array.from(totalsMap.keys());

    if (allUserIds.length === 0) {
      return { data: [], error: null };
    }

    const { data: usersData, error: usersError } = await supabase
      .from('users')
      .select('user_id, username')
      .in('user_id', allUserIds);

    if (usersError) throw usersError;

    const standings = allUserIds.map((userId) => {
      const totals = totalsMap.get(userId) ?? createEmptyTotals();
      const user = usersData?.find((row) => row.user_id === userId);

      // Get player stats for this user
      const userPlayerStats: LivePlayerStats[] = [];
      playerStatsMap.forEach((stats, playerId) => {
        const rosterEntry = rosterMap.get(playerId);
        if (rosterEntry && rosterEntry.userId === userId && stats.totalPoints > 0) {
          userPlayerStats.push(stats);
        }
      });

      // Sort players by total points descending
      userPlayerStats.sort((a, b) => b.totalPoints - a.totalPoints);

      return {
        user_id: userId,
        username: user?.username || 'Unknown User',
        ...totals,
        playerStats: userPlayerStats
      };
    });

    console.log('📈 Final standings:', standings);
    console.log('📊 Total users found:', standings.length);
    return { data: standings, error: null };
  } catch (error: unknown) {
    console.error('❌ Error in getLiveStandings:', error);
    return { data: null, error };
  }
}